import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";

/**
 * Upsert Inventory Policy Classification
 *
 * Writes the CLASSIFICATION family of `im_inventory_policies` — stock status,
 * popularity code, replenishment source, and buyer metadata — for one policy,
 * shaped after the Gently model rather than any source system. The limits
 * family (min/max) has its own step (`upsert_inventory_policy_limits`); the
 * two column sets are field-disjoint by design. See
 * docs/procurement/policy-upsert-single-step-alternative.md for why this is
 * two steps.
 *
 * SOURCE-AGNOSTIC BY DESIGN: the same node ingests an AS/400 CDC table or a
 * `togains` extract file — whatever feeds its sockets. That works because every
 * optional socket is tri-state:
 *
 *   - **unwired / omitted** — this feed does not carry the field, so the column
 *     is left exactly as it is. An unwired socket is genuinely absent (inputs
 *     are seeded from wired edges and static defaults only), so no
 *     source-specific code is needed to express "not mine".
 *   - **null** — the feed carries the field and it is empty, so the column is
 *     CLEARED (an unspecified source is a meaningful state).
 *   - a value — written.
 *
 * That is what lets several feeds write the same policy row without clobbering:
 * an AS/400 stock feed wires `stockStatus` / `popularityCode`, an AS/400 item
 * feed wires only `buyerCode`, and a `togains` INITINFC feed wires
 * `sourceType` / `sourceWarehouseCode` from `giparent1`. Each statement's
 * `DO UPDATE SET` lists only the wired columns.
 *
 * `buyerCode` is the one field where null and omitted agree: it merges into the
 * policy's JSONB metadata per key, so neither deletes a stored buyer_code.
 */
export const upsertInventoryPolicyClassificationStep = defineStepConfig({
  type: "upsert_inventory_policy_classification",
  name: "Upsert Inventory Policy Classification",
  description:
    "Upserts one inventory policy's classification (stock status, popularity code, source type/warehouse, buyer code). Never touches min/max — those belong to Upsert Inventory Policy Limits.",
  category: "Transformation",
  libraryModule: "Procurement",

  inputSchema: z.object({
    // The two identity fields accept NULL, and null is a reported skip rather
    // than a failure. An identity usually arrives from an upstream lookup (a
    // `use_data_mapping` translating a source's own code into a warehouse code,
    // say), and "no match" there is ordinary data — an AS/400 division with no
    // DTLO rows, a code the org has not described yet. Rejecting null would fail
    // the whole chunk over one such row and lose the rows that did resolve,
    // because a chunk shares one input validation pass.
    productSku: z
      .string()
      .nullable()
      .describe(
        "Product SKU (resolved to the product). Null — e.g. from an unmatched upstream lookup — skips the row as unresolvedProduct.",
      ),
    warehouseCode: z
      .string()
      .nullable()
      .describe(
        "Warehouse origin code (resolved to the warehouse). Null — e.g. from an unmatched upstream lookup — skips the row as unresolvedWarehouse.",
      ),
    stockStatus: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Stock status (`stocked` / `non_stock`). Null clears; leave unwired to keep whatever another feed stored.",
      ),
    popularityCode: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Popularity class (e.g. A–G, N — the source's own code). Null clears; leave unwired to keep whatever another feed stored.",
      ),
    sourceType: z
      .enum(["transfer", "buy"])
      .nullable()
      .optional()
      .describe(
        "How this policy replenishes: transfer from a warehouse, or buy from a vendor. Null clears; leave unwired to keep whatever another feed stored.",
      ),
    sourceWarehouseCode: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Origin code of the source warehouse (with sourceType=transfer). Null clears; leave unwired to keep whatever another feed stored.",
      ),
    buyerCode: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Buyer/planner code, merged into policy metadata. Null preserves the existing value.",
      ),
  }),

  outputSchema: z.object({
    written: z.boolean().describe("The policy classification was upserted"),
    skipped: z
      .boolean()
      .describe("The row was skipped because a reference was unresolved"),
    unresolvedProduct: z
      .boolean()
      .describe(
        "No product matched the SKU (ingest the product catalog first)",
      ),
    unresolvedWarehouse: z
      .boolean()
      .describe("No warehouse matched the warehouse code"),
    unresolvedSourceWarehouse: z
      .boolean()
      .describe(
        "A source warehouse code was given but matched no warehouse — the row is skipped, not partially written",
      ),
  }),

  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 5000 }),
    originSystem: z
      .string()
      .optional()
      .describe(
        "Restrict WAREHOUSE resolution to references ingested from this origin system. Does not scope products: ProductV3 is keyed by SKU within the organization and carries no origin system. Leave empty to match any.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
